import { useProfilePreviewData } from "PFApp/use_profile_preview";
import { useCategorizedSkills } from "PFCore/helpers/use_categorized_skills";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag } from "PFCore/types";
import { Profile } from "PFTypes";
import { useMemo } from "react";

import { ProfilePreviewCategorizedSkills } from "./profile_preview_categorized_skills";
import { ProfilePreviewFlatSkills } from "./profile_preview_flat_skills";
import { isSkillsSectionRenderable, SkillsList, splitTopSkills } from "./profile_preview_skills.utils";
import { ProfilePreviewTopSkills } from "./profile_preview_top_skills";

type ProfilePreviewSkillsProps = {
  masterExpand: boolean;
  setMasterExpand: (value: boolean) => void;
};

export const ProfilePreviewSkills = ({ masterExpand, setMasterExpand }: ProfilePreviewSkillsProps) => {
  const {
    data: { profile, ready }
  } = useProfilePreviewData();
  const isSkillCategoriesFeatureEnabled = useIsFeatureEnabled(FeatureFlag.ShowSkillCategories);

  const [skills, topSkills] = useMemo(() => splitTopSkills(profile), [profile]);
  const [skillCategories, other] = useCategorizedSkills(skills, profile?.skillCategories ?? []);
  const hasSkillCategories = Object.keys(skillCategories).length > 0;

  // permissionsGroup check probably not needed because "ready" is taking care but left just in case
  if (!isSkillsSectionRenderable(ready, profile, skills, topSkills)) {
    return null;
  }

  return (
    <>
      <div key="ProfilePreviewCustomFields.top_skills">
        <ProfilePreviewTopSkills topSkills={topSkills} profileId={profile.id} />
      </div>
      <div key="ProfilePreviewCustomFields.skills">
        <ProfilePreviewSkillsSection
          showCategorizedSkills={isSkillCategoriesFeatureEnabled && hasSkillCategories}
          skillCategories={skillCategories}
          other={other}
          topSkills={topSkills}
          skills={skills}
          masterExpand={masterExpand}
          setMasterExpand={setMasterExpand}
          profile={profile}
        />
      </div>
    </>
  );
};

type ProfilePreviewSkillsSectionProps = {
  showCategorizedSkills: boolean;
  skillCategories: ReturnType<typeof useCategorizedSkills>[0];
  other: ReturnType<typeof useCategorizedSkills>[1];
  topSkills: SkillsList;
  skills: SkillsList;
  masterExpand: boolean;
  setMasterExpand: (value: boolean) => void;
  profile: Profile;
};

const ProfilePreviewSkillsSection = ({
  showCategorizedSkills,
  skillCategories,
  other,
  topSkills,
  skills,
  masterExpand,
  setMasterExpand,
  profile
}: ProfilePreviewSkillsSectionProps) =>
  showCategorizedSkills ? (
    <ProfilePreviewCategorizedSkills
      skillCategories={skillCategories}
      other={other}
      hasTopSkills={topSkills.length > 0}
      masterExpand={masterExpand}
      setMasterExpand={setMasterExpand}
      profile={profile}
    />
  ) : (
    <ProfilePreviewFlatSkills skills={skills} profileId={profile.id} />
  );
